import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import useDeptCounts from "@/hooks/useDeptCounts";

/**
 * DepartmentSelectGrid — premium image grid of departments (mobile: 2-col).
 * Used on the Browse page (/browse) so customers can pick a shop visually
 * the way they would on a luxury multi-vertical marketplace.
 */
const FALLBACKS = {
  flowers: "https://images.unsplash.com/photo-1561181286-d3fee7d55364?w=800&q=80",
  perfumes: "https://images.unsplash.com/photo-1541643600914-78b084683601?w=800&q=80",
  electronics: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  household: "https://images.unsplash.com/photo-1556909114-44e3e70034e2?w=800&q=80",
  toys: "https://images.unsplash.com/photo-1558060370-d6019bea3a35?w=800&q=80",
  stationery: "https://images.unsplash.com/photo-1456735190827-d1262f71b8a3?w=800&q=80",
  pharmacy: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80",
};

export default function DepartmentSelectGrid({ onSelect, layout = "grid" }) {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const _b = localStorage.getItem("rg_branch_id") || "";
        const { data } = await http.get(`/departments?country=${localStorage.getItem("rg_country") || "QA"}${_b ? `&branch_id=${_b}` : ""}`);
        if (alive) setDepartments(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
           data-testid="dept-grid-loading">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-[4/5] rounded-[26px] skeleton" />
        ))}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 sm:gap-6 ${
      layout === "compact"
        ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-7"
        : "grid-cols-2 lg:grid-cols-3"
    }`}
         data-testid="department-select-grid">
      {departments.map((d, i) => (
        <DeptCard key={d.id} department={d} onSelect={onSelect} idx={i} />
      ))}
    </div>
  );
}

function DeptCard({ department, onSelect, idx = 0 }) {
  const { lang } = useApp();
  const deptCounts = useDeptCounts();
  const count = deptCounts[department.id] || 0;
  const name = lang === "ar"
    ? (department.name_ar || department.name_en || "")
    : (department.name_en || department.name_ar || "");
  // Prefer the admin-uploaded cover image; fall back to a curated stock photo.
  const cover = department.cover_image || FALLBACKS[department.slug];
  const Chevron = lang === "ar" ? ChevronLeft : ChevronRight;

  const card = (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.5) }}
      data-testid={`dept-card-${department.slug}`}
      className="group relative rounded-[26px] overflow-hidden transition-all duration-300
                 ring-1 ring-black/5 hover:-translate-y-1.5 hover:shadow-2xl
                 hover:ring-2 hover:ring-[#11392A]"
      style={{ background: "var(--bg-card)", boxShadow: "0 10px 28px -14px rgba(0,0,0,0.4)" }}
    >
      <div className="aspect-[4/5] overflow-hidden relative"
           style={{ background: "var(--bg-subtle)" }}>
        {cover ? (
          <img
            src={cover}
            alt={name}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const fb = FALLBACKS[department.slug];
              if (fb && e.currentTarget.src !== fb) { e.currentTarget.src = fb; }
              else { e.currentTarget.style.display = "none"; }
            }}
            className="w-full h-full object-cover transition-transform duration-700
                       group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl opacity-40">
            {department.icon || "🛍️"}
          </div>
        )}

        {/* Brand-green gradient — premium & on-brand, keeps the title legible */}
        <div className="absolute inset-0 pointer-events-none
                        bg-gradient-to-t from-[#0b2b1f] via-[#0b2b1f]/25 to-transparent" />
        {/* Thin gold hairline at the top edge */}
        <div className="absolute top-0 inset-x-0 h-[3px] pointer-events-none
                        bg-gradient-to-r from-transparent via-[#C9A24B] to-transparent opacity-80" />

        {/* Title block sitting on the image */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-white">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center
                             text-lg sm:text-2xl shrink-0 shadow-md"
                  style={{ background: "rgba(11,46,33,0.92)" }}>
              {department.icon || "🎁"}
            </span>
            <h3 className="font-extrabold text-lg sm:text-2xl leading-tight drop-shadow-lg">
              {name}
            </h3>
          </div>
          <div className="mt-2.5 inline-flex items-center gap-1 text-[12px] sm:text-sm font-bold
                          transition-transform duration-300 group-hover:translate-x-[-2px]"
               style={{ color: "#EBD9BF" }}>
            {count > 0 && <span>{count} {lang === "ar" ? "منتج" : "products"} ·</span>}
            {lang === "ar" ? "تسوّق الآن" : "Shop now"}
            <Chevron size={15} />
          </div>
        </div>
      </div>
    </motion.article>
  );

  if (onSelect) {
    return (
      <button onClick={() => onSelect(department)} className="text-start w-full"
              data-testid={`dept-card-btn-${department.slug}`}>
        {card}
      </button>
    );
  }
  return (
    <Link to={`/shop?department=${department.slug}`}
          data-testid={`dept-card-link-${department.slug}`}>
      {card}
    </Link>
  );
}
